import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Json } from "@obsn/shared/db";
import { db } from "../../kern/supabase";
import type { Art, Posten, UnterlageKurz } from "./logik";

/* Daten des Budgets (Paket D1 und D2). Lesen per RLS: Posten und Summen sehen nur Vorstand mit finanzen.verwalten und die Leitung des
   Vorhabens, alle anderen bekommen keine Zeile. Schreiben nur über die RPCs aus 20261015130100_finanzen_budget.sql. */

type Zeile = Record<string, unknown>;

export function alsPosten(z: Zeile): Posten {
  return {
    id: String(z.id),
    art: z.art === "einnahme" ? "einnahme" : "ausgabe",
    kategorie: String(z.kategorie ?? "sonstiges"),
    titel: String(z.titel ?? ""),
    menge: Number(z.menge ?? 1),
    einzelpreis_cent: Number(z.einzelpreis_cent ?? 0),
    plan_cent: Number(z.plan_cent ?? 0),
    ist_cent: z.ist_cent === null || z.ist_cent === undefined ? null : Number(z.ist_cent),
    status: String(z.status ?? "geplant"),
    unterlage_id: (z.unterlage_id as string | null) ?? null,
    notiz: String(z.notiz ?? ""),
  };
}

const schluessel = {
  alle: ["finanzen"] as const,
  posten: (vorhaben: string) => ["finanzen", "posten", vorhaben] as const,
  unterlagen: (vorhaben: string) => ["finanzen", "unterlagen", vorhaben] as const,
  summen: ["finanzen", "summen"] as const,
};

export function usePosten(vorhaben: string) {
  return useQuery({
    queryKey: schluessel.posten(vorhaben),
    queryFn: async (): Promise<Posten[]> => {
      const { data, error } = await db()
        .from("finanzen_posten")
        .select("id, art, kategorie, titel, menge, einzelpreis_cent, plan_cent, ist_cent, status, unterlage_id, notiz")
        .eq("vorhaben_id", vorhaben)
        .order("angelegt_am")
        .order("id");
      if (error) throw error;
      return (data as Zeile[]).map(alsPosten);
    },
  });
}

/** Unterlagen zum Verknüpfen. Ändern sie sich im Reiter „Unterlagen“, lädt der Reiter „Budget“ sie beim nächsten Öffnen neu. */
export function useUnterlagenKurz(vorhaben: string) {
  return useQuery({
    queryKey: schluessel.unterlagen(vorhaben),
    staleTime: 0,
    queryFn: async (): Promise<UnterlageKurz[]> => {
      const { data, error } = await db().from("aufgaben_vorhaben_unterlagen").select("id, art, titel, quelle, url").eq("vorhaben_id", vorhaben).order("art").order("titel");
      if (error) throw error;
      return (data ?? []).map((u) => ({ id: u.id, art: u.art, titel: u.titel, quelle: u.quelle === "datei" ? "datei" : "link", url: u.url }));
    },
  });
}

/** Ergebnis Plan je Vorhaben für die Karten unter /vorhaben. Leer für alle ohne Budget-Rechte. */
export function useErgebnisPlanJeVorhaben() {
  return useQuery({
    queryKey: schluessel.summen,
    staleTime: 30_000,
    queryFn: async (): Promise<ReadonlyMap<string, number>> => {
      const { data, error } = await db().from("finanzen_vorhaben_summen").select("vorhaben_id, ergebnis_plan_cent");
      if (error) throw error;
      return new Map((data ?? []).flatMap((z) => (z.vorhaben_id ? [[z.vorhaben_id, Number(z.ergebnis_plan_cent ?? 0)] as const] : [])));
    },
  });
}

export interface NeuerPosten {
  art: Art;
  kategorie: string;
  titel: string;
  menge: number;
  einzelpreis_cent: number;
  ist_cent: number | null;
  status: string;
  unterlage_id: string | null;
  notiz: string;
}

/** Felder, die sich einzeln speichern lassen (finanzen_posten_bearbeiten) */
export type PostenAenderung = Partial<Pick<Posten, "art" | "kategorie" | "titel" | "menge" | "einzelpreis_cent" | "ist_cent" | "status" | "unterlage_id" | "notiz">>;

export function usePostenAktionen(vorhaben: string) {
  const qc = useQueryClient();
  const neuLaden = () => qc.invalidateQueries({ queryKey: schluessel.alle });
  return {
    anlegen: useMutation({
      mutationFn: async (p: NeuerPosten): Promise<string> => {
        const { data, error } = await db().rpc("finanzen_posten_anlegen", {
          p_vorhaben: vorhaben,
          p_art: p.art,
          p_kategorie: p.kategorie,
          p_titel: p.titel,
          p_menge: p.menge,
          p_einzelpreis_cent: p.einzelpreis_cent,
          ...(p.ist_cent === null ? {} : { p_ist_cent: p.ist_cent }),
          p_status: p.status,
          ...(p.unterlage_id ? { p_unterlage: p.unterlage_id } : {}),
          p_notiz: p.notiz,
        });
        if (error) throw error;
        return String(data);
      },
      onSettled: neuLaden,
    }),
    /** Einen neuen Status zeigt die Statuswahl sofort, bei einem Fehler springt er zurück. Andere Felder stehen nach der Antwort da. */
    bearbeiten: useMutation({
      mutationFn: async ({ id, aenderung }: { id: string; aenderung: PostenAenderung }) => {
        const { error } = await db().rpc("finanzen_posten_bearbeiten", { p_id: id, p_aenderung: aenderung as Json });
        if (error) throw error;
      },
      onMutate: async ({ id, aenderung }: { id: string; aenderung: PostenAenderung }) => {
        const status = aenderung.status;
        if (status === undefined) return undefined;
        await qc.cancelQueries({ queryKey: schluessel.posten(vorhaben) });
        const vorher = qc.getQueryData<Posten[]>(schluessel.posten(vorhaben));
        qc.setQueryData<Posten[]>(schluessel.posten(vorhaben), (alt) => alt?.map((p) => (p.id === id ? { ...p, status } : p)));
        return { vorher };
      },
      onError: (_e, _v, k) => {
        if (k?.vorher) qc.setQueryData(schluessel.posten(vorhaben), k.vorher);
      },
      onSettled: neuLaden,
    }),
    loeschen: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await db().rpc("finanzen_posten_loeschen", { p_id: id });
        if (error) throw error;
      },
      onSettled: neuLaden,
    }),
  };
}
